import type { Cents } from "../types/money";

/**
 * Inkoopfacturen van een leverancier (nu: Timmers) — registreren, controleren, betalen.
 *
 * De keten is die van elk purchase-to-pay-proces: factuur binnen → registreren → vergelijken met
 * wat wij verwachtten te betalen → goedkeuren → mee in de betaalrun → betaald. De bank betaalt
 * nooit vanuit deze webshop; we leveren een betaalbestand aan dat in de bankomgeving getekend
 * wordt (bij Rabobank met de scanner).
 *
 * Alles wat hier staat is puur rekenwerk en regels — client en Cloud Functions gebruiken het
 * allebei, zodat het scherm en de server niet uit elkaar kunnen lopen.
 */

/** Leveranciers waarvoor we facturen bijhouden. */
export type SupplierId = "timmers";

export const SUPPLIER_INVOICE_STATUSES = ["nieuw", "goedgekeurd", "ingepland", "betaald", "betwist"] as const;
export type SupplierInvoiceStatus = (typeof SUPPLIER_INVOICE_STATUSES)[number];

export interface SupplierInvoice {
  id: string;
  supplier: SupplierId;
  /** Factuurnummer van de leverancier — uniek per leverancier; dat is de dubbelbetaal-guard. */
  invoiceNumber: string;
  invoiceDate: number;
  dueDate: number;
  amountExclCents: Cents;
  vatCents: Cents;
  amountInclCents: Cents;
  /** Rekening waarop we betalen, plus wat er op het afschrift moet komen. */
  iban: string;
  tenaamstelling: string;
  kenmerk: string;
  /** Onze eigen bestellingen die op deze factuur staan. */
  orderIds: string[];
  /** Ons verwachte inkooptotaal voor die bestellingen, bevroren bij het aanmaken. */
  expectedCents: Cents | null;
  status: SupplierInvoiceStatus;
  /** Pad in Firebase Storage naar de PDF van de leverancier. */
  pdfPath?: string;
  note?: string;
  createdAt: number;
  createdBy: string;
  approvedAt?: number;
  approvedBy?: string;
  paidAt?: number;
  paymentRunId?: string;
  /** Boekingsnummer in Exact Online zodra de factuur daar is doorgeboekt (voorkomt dubbel boeken). */
  exactEntryId?: string;
  exactSyncedAt?: number;
}

export interface PaymentRun {
  id: string;
  createdAt: number;
  createdBy: string;
  /** Datum waarop de bank moet uitvoeren (meestal de eerstvolgende vrijdag). */
  executionDate: number;
  invoiceIds: string[];
  totalCents: Cents;
  status: "klaar" | "bevestigd";
  confirmedAt?: number;
  confirmedBy?: string;
}

/**
 * Toegestane statusovergangen. Een factuur kan alleen ingepland worden als hij is goedgekeurd, en
 * betaald alleen vanuit een run — zo kan er nooit iets betaald worden dat niemand bekeken heeft.
 */
export const INVOICE_TRANSITIONS: Record<SupplierInvoiceStatus, SupplierInvoiceStatus[]> = {
  nieuw: ["goedgekeurd", "betwist"],
  betwist: ["goedgekeurd", "nieuw"],
  goedgekeurd: ["ingepland", "betwist", "nieuw"],
  ingepland: ["betaald", "goedgekeurd"],
  betaald: [],
};

export function mayTransition(from: SupplierInvoiceStatus, to: SupplierInvoiceStatus): boolean {
  return INVOICE_TRANSITIONS[from].includes(to);
}

/** Mag deze factuur mee in een betaalrun? */
export function isPayable(inv: Pick<SupplierInvoice, "status">): boolean {
  return inv.status === "goedgekeurd";
}

/**
 * Mag deze factuur rechtstreeks op "betaald" zonder onze eigen betaalrun? Alleen als hij in de
 * boekhouding staat: dan wordt er in Exact betaald en afgeletterd, en is onze run niet meer aan
 * de beurt. Zonder die boeking blijft de regel staan dat betalen via een run gaat, zodat er nooit
 * iets als betaald wegvalt waar niemand naar gekeken heeft.
 */
export function magDirectOpBetaald(inv: Pick<SupplierInvoice, "status" | "exactEntryId">): boolean {
  return inv.status === "goedgekeurd" && !!inv.exactEntryId;
}

/**
 * Verschil tussen wat de leverancier factureert en wat wij verwachtten, in centen en procenten.
 * Zonder verwachting (geen gekoppelde bestelling) geven we null — dan is er niets te vergelijken
 * en moet de mens beslissen.
 */
export interface FactuurVerschil {
  cents: number;
  pct: number;
  /** Binnen de marge die we zonder toelichting accepteren. */
  binnenMarge: boolean;
}

/** Afrondings- en transportverschillen tot 2% laten we zonder toelichting door. */
export const VERSCHIL_MARGE_PCT = 2;

export function factuurVerschil(amountInclCents: number, expectedCents: number | null | undefined): FactuurVerschil | null {
  if (expectedCents == null || expectedCents <= 0) return null;
  const cents = amountInclCents - expectedCents;
  const pct = (cents / expectedCents) * 100;
  return { cents, pct, binnenMarge: Math.abs(pct) <= VERSCHIL_MARGE_PCT };
}

/**
 * IBAN-controle volgens de standaard mod-97-toets (ISO 13616). Vangt tikfouten en omgedraaide
 * cijfers — precies waar een betaling op de verkeerde rekening belandt.
 */
export function isValidIban(raw: string): boolean {
  const iban = (raw || "").replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const herschikt = iban.slice(4) + iban.slice(0, 4);
  const cijfers = herschikt.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  // Stukje voor stukje modulo, anders loopt het getal buiten het bereik van Number.
  let rest = 0;
  for (const teken of cijfers) rest = (rest * 10 + Number(teken)) % 97;
  return rest === 1;
}

/** Nette weergave: NL12 RABO 0123 4567 89 */
export function formatIban(raw: string): string {
  return (raw || "").replace(/\s+/g, "").toUpperCase().replace(/(.{4})/g, "$1 ").trim();
}

/**
 * De eerstvolgende vrijdag (of vandaag, als het vrijdag is) — de vaste betaaldag. Werkt op een
 * meegegeven "nu", zodat het testbaar blijft en de server niet van de clientklok afhangt.
 */
export function eerstvolgendeVrijdag(nu: number): number {
  const d = new Date(nu);
  d.setHours(12, 0, 0, 0);
  const dagenTotVrijdag = (5 - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + dagenTotVrijdag);
  return d.getTime();
}

/**
 * Facturen die in de betaalrun van deze datum horen: goedgekeurd én uiterlijk `dagenVooruit` na de
 * uitvoerdatum vervallend. Zo betaal je niet te vroeg (werkkapitaal) maar ook nooit te laat.
 */
export function voorBetaalrun(
  facturen: SupplierInvoice[],
  executionDate: number,
  dagenVooruit = 7,
): SupplierInvoice[] {
  const grens = executionDate + dagenVooruit * 86_400_000;
  return facturen.filter((f) => isPayable(f) && f.dueDate <= grens);
}

/** Totaal van een set facturen (incl. btw — dat is wat er betaald wordt). */
export function betaalrunTotaal(facturen: Pick<SupplierInvoice, "amountInclCents">[]): Cents {
  return facturen.reduce((n, f) => n + f.amountInclCents, 0) as Cents;
}
